import React, { useState } from "react";
import { Send, MessageCircle, Mail } from "lucide-react";
import apiClient from "../../utils/apiClient";
import { AppModal, AppButton } from "../ui";
import "./attendance.scss";

/**
 * NotifyGuardiansModal — admin-initiated guardian alert sender (WhatsApp + Email).
 * Reused by Daily Absentees and Low Attendance. Posts to `endpoint` with the
 * page's `payload` plus the chosen channels, then shows a per-channel result.
 *
 * Props:
 *   show, onHide
 *   endpoint  — e.g. "/attendance/notify-absentees"
 *   payload   — e.g. { date } or { threshold, batchId }
 *   count     — number of recipients (for the button label)
 *   title     — modal title
 */
const NotifyGuardiansModal = ({ show, onHide, endpoint, payload, count = 0, title = "Notify Guardians" }) => {
  const [channels, setChannels] = useState({ whatsapp: true, email: true });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const selected = Object.keys(channels).filter((k) => channels[k]);
  const toggle = (k) => setChannels((c) => ({ ...c, [k]: !c[k] }));

  const handleSend = async () => {
    setSending(true);
    setResult(null);
    try {
      const { data } = await apiClient.post(endpoint, { ...payload, channels: selected });
      setResult(data);
    } catch (err) {
      setResult({ error: err.response?.data?.message || "Failed to send notifications." });
    }
    setSending(false);
  };

  const close = () => {
    setResult(null);
    setChannels({ whatsapp: true, email: true });
    onHide?.();
  };

  const done = result && !result.error;
  const s = result?.summary;

  return (
    <AppModal
      show={show}
      onHide={close}
      title={title}
      icon={<Send size={18} />}
      size="sm"
      footer={
        done ? (
          <AppButton variant="primary" onClick={close}>Done</AppButton>
        ) : (
          <>
            <AppButton variant="outline" onClick={close} disabled={sending}>Cancel</AppButton>
            <AppButton
              variant="primary"
              onClick={handleSend}
              disabled={sending || selected.length === 0 || count === 0}
            >
              {sending ? "Sending…" : <><Send size={15} /> Send to {count} guardian{count !== 1 ? "s" : ""}</>}
            </AppButton>
          </>
        )
      }
    >
      {done ? (
        <div className="ng-result">
          {selected.includes("whatsapp") && s?.whatsapp && (
            <p><MessageCircle size={14} /> WhatsApp — <strong>{s.whatsapp.sent}</strong> sent, {s.whatsapp.skipped} skipped, {s.whatsapp.failed} failed</p>
          )}
          {selected.includes("email") && s?.email && (
            <p><Mail size={14} /> Email — <strong>{s.email.sent}</strong> sent, {s.email.skipped} skipped, {s.email.failed} failed</p>
          )}
          <p className="ng-hint">“Skipped” means no guardian contact is on file for that channel.</p>
        </div>
      ) : (
        <>
          <p className="ng-intro">
            Send an alert to the <strong>guardian</strong> of each of the {count} listed student{count !== 1 ? "s" : ""}.
            Students without a guardian contact are skipped.
          </p>
          <label className="ng-channel">
            <input type="checkbox" checked={channels.whatsapp} onChange={() => toggle("whatsapp")} />
            <MessageCircle size={15} /> WhatsApp
            <span className="ng-channel-note">requires an approved template</span>
          </label>
          <label className="ng-channel">
            <input type="checkbox" checked={channels.email} onChange={() => toggle("email")} />
            <Mail size={15} /> Email
          </label>
          {result?.error && <p className="ng-error">{result.error}</p>}
        </>
      )}
    </AppModal>
  );
};

export default NotifyGuardiansModal;
